import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { artistsDetailData } from '../../mocks/artistsDetail';
import ArtistsDetailHeader from './components/ArtistsDetailHeader';
import ArtistsDetailFooter from './components/ArtistsDetailFooter';
import ArtistCard from './components/ArtistCard';

const genres = ['すべて', ...Array.from(new Set(artistsDetailData.flatMap(a => a.genre.split(' / '))))];
const areas = ['すべて', ...Array.from(new Set(artistsDetailData.map(a => a.area)))];

export default function ArtistsDetailPage() {
  const [selectedGenre, setSelectedGenre] = useState('すべて');
  const [selectedArea, setSelectedArea] = useState('すべて');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredArtists = useMemo(() => {
    return artistsDetailData.filter(artist => {
      const matchGenre = selectedGenre === 'すべて' || artist.genre.includes(selectedGenre);
      const matchArea = selectedArea === 'すべて' || artist.area === selectedArea;
      const matchSearch = searchQuery === '' ||
        artist.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        artist.genre.includes(searchQuery) ||
        artist.area.includes(searchQuery);
      return matchGenre && matchArea && matchSearch;
    });
  }, [selectedGenre, selectedArea, searchQuery]);

  return (
    <div className="min-h-screen bg-white">
      <ArtistsDetailHeader />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
              <Link to="/" className="hover:text-gray-900 transition-colors cursor-pointer">トップページ</Link>
              <i className="ri-arrow-right-s-line text-gray-400"></i>
              <span className="text-gray-900 font-medium">登録アーティスト一覧（詳細）</span>
            </div>

            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              <a href="#artists-detail-list" className="hover:opacity-80 transition-opacity">登録アーティスト一覧</a>
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl leading-relaxed mb-6">
              東京支部に登録しているアーティストの一覧ページです。各アーティストの公式サイトやSNSへのリンクを掲載しています。最新のライブスケジュールやチケット情報は各アーティストの公式ページでご確認ください。
            </p>

            <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-3xl">
              <h2 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-information-line text-gray-600"></i>
                ご注意
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                本ページの掲載は各アーティストからの情報提供を基にしています。ライブスケジュール・チケット販売状況等は公式サイトで必ずご確認ください。
              </p>
              <p className="text-sm text-gray-600 leading-relaxed">
                なお、サイト制作や表示内容の修正依頼は「支部窓口」より承ります（支部窓口フォーム／提案準備窓口をご利用ください）。
              </p>
            </div>
          </div>
        </section>

        {/* Filter & Search */}
        <section className="py-8 border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col gap-4">
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-2">ジャンル</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {genres.map(genre => (
                    <button
                      key={genre}
                      onClick={() => setSelectedGenre(genre)}
                      className={`px-4 py-2 text-sm rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                        selectedGenre === genre
                          ? 'bg-gray-900 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {genre}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-2">エリア</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {areas.map(area => (
                      <button
                        key={area}
                        onClick={() => setSelectedArea(area)}
                        className={`px-4 py-2 text-sm rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                          selectedArea === area
                            ? 'bg-gray-900 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {area}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="w-full md:w-72">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-2">検索</p>
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                    <input
                      type="text"
                      placeholder="アーティスト名・ジャンル・エリアで検索"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-md text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Artists List */}
        <section id="artists-detail-list" className="py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center justify-between mb-8">
              <p className="text-sm text-gray-500">
                <strong className="text-gray-900">{filteredArtists.length}</strong> 組のアーティストが見つかりました
              </p>
            </div>

            {filteredArtists.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArtists.map((artist, index) => (
                  <ArtistCard key={index} artist={artist} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                  <i className="ri-search-line text-2xl text-gray-400"></i>
                </div>
                <p className="text-gray-500 mb-2">該当するアーティストが見つかりませんでした</p>
                <button
                  onClick={() => { setSelectedGenre('すべて'); setSelectedArea('すべて'); setSearchQuery(''); }}
                  className="text-sm text-gray-900 underline hover:no-underline cursor-pointer whitespace-nowrap"
                >
                  フィルターをリセット
                </button>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">アーティスト登録をご検討の方へ</h2>
            <p className="text-gray-600 mb-8 leading-relaxed">
              東京支部への登録にご興味のあるアーティストの方は、参加希望の会場を通じて登録手続きを行ってください。まずはお気軽にお問い合わせください。
            </p>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-3 rounded-md text-sm font-semibold hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-send-line"></i>
              お問い合わせはこちら
            </a>
          </div>
        </section>
      </main>

      <ArtistsDetailFooter />
    </div>
  );
}
